import { useState, useEffect } from 'react';
import { useOne } from '@refinedev/core';
import { Card, Spin, Alert, Typography, Space, Tag, Row, Col, Divider, Tooltip } from 'antd';
import { 
  UserOutlined, 
  CalendarOutlined, 
  PhoneOutlined, 
  MailOutlined,
  ClockCircleOutlined,
  MedicineBoxOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { FaWhatsapp } from 'react-icons/fa';
// Reuse renderers from table columns for consistent UI
import { useStatusRender as useAppointmentStatusRender } from '@src/pages/appointments/hooks/useStatusRender';
import { useServiceTypeRender } from '@src/pages/appointments/hooks/useServiceTypeRender';
import { useScheduledDateRender as useAppointmentScheduledRender } from '@src/hooks';
import { useStatusRender as useLeadStatusRender } from '@src/pages/leads/hooks/useStatusRender';
import { useEngagementModeRender } from '@src/pages/leads/hooks/useEngagementModeRender';
import { useSourceRender } from '@src/pages/leads/hooks/useSourceRender';
import { useGenderRender } from '@src/pages/patients/hooks/useGenderRender';
import { useTypeRender } from '@src/pages/patients/hooks/useTypeRender';
import { useStatusRender } from '@src/pages/patients/hooks/useStatusRender';
import { THERAPIST_TYPE_COLORS } from '@src/pages/patients/details/tabs/treatments/constants';
import { useConversionStatusRender } from '@src/pages/patients/hooks/useConversionStatusRender';

const { Text, Title } = Typography;

interface ReferenceDetailsProps {
  type: string;
  referenceId: string | number;
}

export const useReferenceDetails = ({ type, referenceId }: ReferenceDetailsProps) => {
  const [shouldFetch, setShouldFetch] = useState(false);
  // init render helpers
  const getAppointmentStatusConfig = useAppointmentStatusRender();
  const getServiceTypeConfig = useServiceTypeRender();
  const renderScheduledDate = useAppointmentScheduledRender();
  const getLeadStatusConfig = useLeadStatusRender();
  const getEngagementModeConfig = useEngagementModeRender();
  const getSourceConfig = useSourceRender();
  const getGenderConfig = useGenderRender();
  const getTypeConfig = useTypeRender();
  const getStatusRender = useStatusRender();
  const getConversionStatusConfig = useConversionStatusRender();
  // Determine the resource based on type
  const getResource = (type: string) => {
    switch (type) {
      case 'APPOINTMENT':
        return 'appointments';
      case 'PATIENT':
        return 'patients';
      case 'LEAD':
        return 'leads';
      case 'FEEDBACK':
        return 'treatments';
      default:
        return null;
    }
  };

  const resource = getResource(type);

  // Fetch reference details
  const { data, isLoading, error } = useOne({
    resource: resource || '',
    id: referenceId || '',
    queryOptions: {
      enabled: shouldFetch && !!resource && !!referenceId,
    },
  });

  // Enable fetching when both type and referenceId are available
  useEffect(() => {
    if (type && referenceId && resource) {
      setShouldFetch(true);
    } else {
      setShouldFetch(false);
    }
  }, [type, referenceId, resource]);

  const renderDetails = () => {
    if (!shouldFetch || !resource || !referenceId) {
      return null;
    }

    if (isLoading) {
      return (
        <Card size="small" style={{ marginTop: 16 }}>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="small" />
            <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
              Loading {type.toLowerCase()} details...
            </div>
          </div>
        </Card>
      );
    }

    if (error) {
      return (
        <Card size="small" style={{ marginTop: 16 }}>
          <Alert
            message={`Failed to load ${type.toLowerCase()} details`}
            description="Please check if the reference ID is correct."
            type="warning"
            showIcon
          />
        </Card>
      );
    }

    const details = data?.data;
    if (!details) {
      return (
        <Card size="small" style={{ marginTop: 16 }}>
          <Alert
            message={`No ${type.toLowerCase()} found`}
            description="Please check the reference ID."
            type="error"
            showIcon
          />
        </Card>
      );
    }

    // Helper: consistent Patient Info block (name + phone + WhatsApp)
    const renderPatientInfo = (name?: string | null, phone?: string | null) => (
      <div>
        <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
          PATIENT INFO
        </Text>
        <br />
        <div style={{ fontSize: '14px', color: '#262626', fontWeight: 600 }}>
          {name?.toString().trim() || 'N/A'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
          <div style={{ fontSize: '13px', color: '#595959' }}>+{phone || 'No mobile'}</div>
          {phone && (
            <Tooltip title="Open WhatsApp">
              <FaWhatsapp
                style={{ fontSize: '18px', color: '#25D366', cursor: 'pointer' }}
                onClick={e => {
                  e.stopPropagation();
                  if (phone) {
                    window.open(
                      `https://web.whatsapp.com/send/?phone=${phone}`,
                      '_blank'
                    );
                  }
                }}
              />
            </Tooltip>
          )}
        </div>
      </div>
    );

    // Render details based on type
    switch (type) {
      case 'APPOINTMENT':
        return (
          <Card 
            style={{ 
              marginTop: 16,
              minHeight: '300px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
            title={
              <Space>
                <CalendarOutlined style={{ color: '#1890ff' }} />
                <Text strong style={{ fontSize: '16px' }}>Appointment Details</Text>
              </Space>
            }
            bodyStyle={{ padding: '20px' }}
          >
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <div style={{ marginBottom: '5px' }}>
                  {renderPatientInfo(details.patient_name, details.patient_mobile_no)}
                </div>
              </Col>
              
              <Col span={6}>
                <div style={{ marginBottom: '5px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    SCHEDULED DATE & TIME
                  </Text>
                  <br />
                  <div>
                    {details.scheduled_datetime
                      ? renderScheduledDate(details.scheduled_datetime, details.status)
                      : '-'}
                  </div>
                </div>
              </Col>

              <Col span={6}>
                <div style={{ marginBottom: '5px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    SERVICE TYPE
                  </Text>
                  <br />
                  <div>{details.service_type ? getServiceTypeConfig(details.service_type) : '-'}</div>
                </div>
              </Col>
              
              <Col span={6}>
                <div style={{ marginBottom: '5px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    STATUS
                  </Text>
                  <br />
                  {details.status ? (() => { const cfg = getAppointmentStatusConfig(details.status); return (
                    <Tag icon={cfg.icon} style={{ fontWeight: 500, backgroundColor: cfg.bgColor, color: cfg.textColor, borderColor: cfg.borderColor, border: `1px solid ${cfg.borderColor}` }}>{cfg.text}</Tag>
                  ); })() : '-'}
                </div>
              </Col>
              
              
              <Col span={12}>
                <div style={{ marginBottom: '5px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    DOCTOR
                  </Text>
                  <br />
                  <Text style={{ fontSize: '14px', color: '#262626' }}>
                    {details.appointed_to?.first_name || '-'} {details.appointed_to?.last_name || ''}
                  </Text>
                </div>
              </Col>

              
              {details.notes && (
                <Col span={24}>
                  <Divider style={{ margin: '5px 0 5px 0' }} />
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                      NOTES
                    </Text>
                    <br />
                    <Text style={{ fontSize: '14px', color: '#262626', lineHeight: '1.5' }}>
                      {details.notes}
                    </Text>
                  </div>
                </Col>
              )}
            </Row>
          </Card>
        );

      case 'PATIENT':
        return (
          <Card 
            style={{ 
              marginTop: 16,
              minHeight: '400px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
            title={
              <Space>
                <UserOutlined style={{ color: '#52c41a' }} />
                <Text strong style={{ fontSize: '16px' }}>Patient Details</Text>
              </Space>
            }
            bodyStyle={{ padding: '20px' }}
          >
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <div style={{ marginBottom: '16px' }}>
                  {renderPatientInfo(
                    details.first_name && details.last_name
                      ? `${details.first_name} ${details.last_name}`
                      : details.patient_name,
                    details.mobile_no
                  )}
                </div>
              </Col>
              
              
              <Col span={6}>
                <div style={{ marginBottom: '16px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    GENDER
                  </Text>
                  <br />
                  <div>{details.gender ? getGenderConfig(details.gender) : '-'}</div>
                </div>
              </Col>

              <Col span={6}>
                <div style={{ marginBottom: '16px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    PATIENT STATUS
                  </Text>
                  <br />
                  <div>{details.patient_status ? getStatusRender(details.patient_status) : '-'}</div>
                </div>
              </Col>

              <Col span={6}>
                <div style={{ marginBottom: '16px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    CONVERSION STATUS
                  </Text>
                  <br />
                  <div>{details.conversion_status ? getConversionStatusConfig(details.conversion_status) : '-'}</div>
                </div>
              </Col>
              
              <Col span={8}>
                <div style={{ marginBottom: '16px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    AGE
                  </Text>
                  <br />
                  <Text style={{ fontSize: '14px', color: '#262626' }}>
                    {details.age} years
                    (Birth Date: {details.birth_date 
                      ? dayjs(details.birth_date).format('MMM DD, YYYY')
                      : 'N/A'
                    })
                  </Text>
                </div>
              </Col>
              
              {details.type && (
                <Col span={8}>
                  <div style={{ marginBottom: '16px' }}>
                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                      PATIENT TYPE
                    </Text>
                    <br />
                    <div>
                      {Array.isArray(details.type)
                        ? details.type.map((t: string, i: number) => (
                            <span key={i} style={{ marginRight: 6 }}>{getTypeConfig(t)}</span>
                          ))
                        : getTypeConfig(details.type as string)}
                    </div>
                  </div>
                </Col>
              )}
              
              {details.referred_by_name && (
                <Col span={8}>
                  <div style={{ marginBottom: '16px' }}>
                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                      REFERRED BY
                    </Text>
                    <br />
                    <Text style={{ fontSize: '14px', color: '#262626' }}>
                      {details.referred_by_name} ({details.referred_by_type})
                    </Text>
                  </div>
                </Col>
              )}
              
              {details.notes && (
                <Col span={24}>
                  <Divider style={{ margin: '16px 0 12px 0' }} />
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                      NOTES
                    </Text>
                    <br />
                    <Text style={{ fontSize: '14px', color: '#262626', lineHeight: '1.5' }}>
                      {details.notes}
                    </Text>
                  </div>
                </Col>
              )}
            </Row>
          </Card>
        );

      case 'LEAD':
        return (
          <Card 
            style={{ 
              marginTop: 16,
              minHeight: '300px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
            title={
              <Space>
                <UserOutlined style={{ color: '#fa8c16' }} />
                <Text strong style={{ fontSize: '16px' }}>Lead Details</Text>
              </Space>
            }
            bodyStyle={{ padding: '20px' }}
          >
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <div style={{ marginBottom: '16px' }}>
                  {renderPatientInfo(details.patient_name, details.patient_mobile_no)}
                </div>
              </Col>
              
             
              
              <Col span={8}>
                <div style={{ marginBottom: '16px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    LEAD SOURCE
                  </Text>
                  <br />
                  <div>{details.source ? getSourceConfig(details.source) : '-'}</div>
                </div>
              </Col>
              
              <Col span={8}>
                <div style={{ marginBottom: '16px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    STATUS
                  </Text>
                  <br />
                  <div>{details.status ? getLeadStatusConfig(details.status) : '-'}</div>
                </div>
              </Col>
              
              <Col span={12} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'}}>
                <div style={{ marginBottom: '16px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    ASSIGNED TO
                  </Text>
                  <br />
                  <Text style={{ fontSize: '14px', color: '#262626' }}>
                    {details.assignee?.first_name || '-'} {details.assignee?.last_name || ''}
                  </Text>
                </div>
                <div style={{ marginBottom: '16px' }}>{details.interest && (
                <Col span={24}>
                  <div style={{ marginBottom: '16px' }}>
                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                      INTEREST
                    </Text>
                    <br />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                      {(Array.isArray(details.interest)
                        ? details.interest
                        : typeof details.interest === 'string'
                        ? details.interest.split(',').filter(Boolean)
                        : []
                      ).map((interest: string, index: number) => (
                        <Tag
                          key={index}
                          color="blue"
                          style={{ margin: 0, fontSize: '11px' }}
                        >
                          {typeof interest === 'string' ? interest.trim() : interest}
                        </Tag>
                      ))}
                    </div>
                  </div>
                </Col>
              )}</div>
              </Col>      
               
              {details.notes && (
                <Col span={24}>
                  <Divider style={{ margin: '5px 0 5px 0' }} />
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                      NOTES
                    </Text>
                    <br />
                    <Text style={{ fontSize: '14px', color: '#262626', lineHeight: '1.5' }}>
                      {details.notes}
                    </Text>
                  </div>
                </Col>
              )}
            </Row>
          </Card>
        );

      case 'FEEDBACK':
        return (
          <Card 
            style={{ 
              marginTop: 16,
              minHeight: '300px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
            title={
              <Space>
                <MedicineBoxOutlined style={{ color: '#722ed1' }} />
                <Text strong style={{ fontSize: '16px' }}>Treatment Details</Text>
              </Space>
            }
            bodyStyle={{ padding: '20px' }}
          >
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <div style={{ marginBottom: '5px' }}>
                  {renderPatientInfo(
                    `${details.patient?.first_name || '-'} ${details.patient?.last_name || ''}`,
                    details.patient?.mobile_no || null
                  )}
                </div>
              </Col>
              
              <Col span={8}>
                <div style={{ marginBottom: '5px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    TREATMENT NAME
                  </Text>
                  <br />
                  <Text style={{ fontSize: '14px', color: '#262626' }}>
                    {details.treatment_name || 'N/A'}
                  </Text>
                </div>
              </Col>
              
              <Col span={8}>
                <div style={{ marginBottom: '5px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    THERAPIST TYPE
                  </Text>
                  <br />
                  <Tag color={(THERAPIST_TYPE_COLORS as Record<string, string>)[details.therapist_type as string] || 'default'} style={{ margin: 0 }}>
                    {details.therapist_type || '-'}
                  </Tag>
                </div>
              </Col>
              
              <Col span={8}>
                <div style={{ marginBottom: '5px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    BODY PART
                  </Text>
                  <br />
                  <Text style={{ fontSize: '14px', color: '#262626' }}>
                    {Array.isArray(details.body_part) ? details.body_part.join(', ') : details.body_part || 'N/A'}
                  </Text>
                </div>
              </Col>
              
              <Col span={8}>
                <div style={{ marginBottom: '5px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    SESSION NUMBER
                  </Text>
                  <br />
                  <Text style={{ fontSize: '14px', color: '#262626' }}>
                    {details.session_number || 'N/A'}
                  </Text>
                </div>
              </Col>
              
                  <Col span={8}>
                <div style={{ marginBottom: '5px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    SKIN TYPE
                  </Text>
                  <br />
                  <Text style={{ fontSize: '14px', color: '#262626' }}>
                    {details.skin_type || 'N/A'}
                  </Text>
                </div>
              </Col>
              
              <Col span={8}>
                <div style={{ marginBottom: '5px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    MACHINE NAME
                  </Text>
                  <br />
                  <Text style={{ fontSize: '14px', color: '#262626' }}>
                    {details.machine_name || 'N/A'}
                  </Text>
                </div>
              </Col>
              
              <Col span={8}>
                <div style={{ marginBottom: '5px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    DOCTOR
                  </Text>
                  <br />
                  <Text style={{ fontSize: '14px', color: '#262626' }}>
                    {details.doctor?.first_name || '-'} {details.doctor?.last_name || ''}
                  </Text>
                </div>
              </Col>
              
              <Col span={8}>
                <div style={{ marginBottom: '16px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    THERAPIST
                  </Text>
                  <br />
                  <Text style={{ fontSize: '14px', color: '#262626' }}>
                    {details.therapist?.first_name || '-'} {details.therapist?.last_name || ''}
                  </Text>
                </div>
              </Col>
              
              {details.remark && (
                <Col span={24}>
                  <Divider style={{ margin: '16px 0 12px 0' }} />
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                      REMARKS
                    </Text>
                    <br />
                    <Text style={{ fontSize: '14px', color: '#262626', lineHeight: '1.5' }}>
                      {details.remark}
                    </Text>
                  </div>
                </Col>
              )}
            </Row>
          </Card>
        );

      default:
        return null;
    }
  };

  return {
    renderDetails,
    isLoading,
    error,
    data: data?.data,
  };
};
